import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { LoadingController, AlertController, ModalController, Platform } from '@ionic/angular';
import { AuthService } from 'src/app/api/core/auth/auth.service';
import { AlertQueueService } from 'src/app/api/utils/prompt-queue/alert-queue.service';
import { CoreService } from 'src/app/api/core/core/core.service';
import { AnalyticsService } from 'src/app/api/utils/analytics/analytics.service';
import { WatcherService } from 'src/app/api/utils/watcher/watcher.service';
import { OPEN_URL_BROWSER } from 'src/app/api/utils/watcher/watcher-event';
import { AppConfigService } from 'src/app/api/core/settings/app-config.service';
import { ModalFormParentComponent } from '../modal-form-parent/modal-form-parent.component';

@Component({
  selector: 'hub-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent extends ModalFormParentComponent implements OnInit {
  @Input() email: string;

  @ViewChild('submitBtn', { static: true, read: ElementRef }) submitBtn: ElementRef;
  private _loader;
  private _fallbackCount: number = 0;

  public formControl: FormControl = new FormControl('', [Validators.required, Validators.email]);
  constructor(
    public loadingCtrl: LoadingController,
    public auth: AuthService,
    public alertQueue: AlertQueueService,
    public alertCtrl: AlertController,
    public core: CoreService,
    public modalCtrl: ModalController,
    public changeRef: ChangeDetectorRef,
    public analytics: AnalyticsService,
    public watcher: WatcherService,
    public config: AppConfigService,
    public platform: Platform,
    public renderer: Renderer2,
  ) {
    super(platform, modalCtrl, renderer);
  }

  ngOnInit() {
    super.ngOnInit();
    this.keyboardParentTarget = this.submitBtn;

    this.formControl.setValue(this.email);
    this.changeRef.detectChanges();
  }

  /**
   * Sends a request to the server to reset a user's password by providing an email
   * @param fallback - A flagger if the verification call is a fallback from previous failed attempt
   */
  async verifyEmail(fallback: boolean = false) {
    if (!fallback) {
      await this.showLoader();
    }

    // get the email
    const email: string = this.formControl.value;

    const payload = await this.auth.resetPassword(email);
    if (payload && payload.success) {
      this._fallbackCount = 0;

      const alert = await this.alertCtrl.create({
        header: 'Reset Password',
        message: 'Please check ' + email + ' for an email which will contain a link for you to change your password.',
        buttons: [
          {
            text: 'Continue',
            handler: () => {
              this.close();
            },
          },
        ],
      });

      this.alertQueue.queueAlert(alert);
      this._dismissLoading();
      this.changeRef.detectChanges();
      return { success: true };
    } else {
      // could be invalid email
      this._handleFailedVerification(payload);
      return { success: false };
    }
  }

  /**
   * Function that handles failed response from email verification
   * @param resp - The http response payload
   */
  async _handleFailedVerification(resp) {
    ++this._fallbackCount;

    let buttons: any[] = [{ text: 'Close' }];
    buttons.push({
      text: 'Live Chat',
      handler: () => {
        this.analytics.showHubspot(resp => {
          if (!resp) this.watcher.broadcast(OPEN_URL_BROWSER, this.config.support_url);
        });
      },
    });

    let email_message = "<span class='errorMessage'>";
    email_message += "<span class='warning'>We were not able to verify your email address.</span>";
    email_message += '<br><br>Please jump on our <b>Live Chat</b> and ask for assistance.';
    email_message += '</span>';

    if (this._fallbackCount === this.core.fallback_check_count) {
      this._fallbackCount = 0;

      // reset
      this.core.setServerByEnv(this.core.Active_server.fallback);

      const alert = await this.alertCtrl.create({
        header: 'Oops!',
        message: email_message,
        buttons: buttons,
      });

      this.alertQueue.queueAlert(alert);
      this._dismissLoading();
    } else {
      // if there's a fallback - then give it a try
      if (this.core.Active_server.fallback) {
        this.core.setServerByEnv(this.core.Active_server.fallback);
        this.verifyEmail(true);
      } else {
        const alert = await this.alertCtrl.create({
          header: 'Oops!',
          message: email_message,
          buttons: buttons,
        });

        this.alertQueue.queueAlert(alert);
        this._dismissLoading();
      }
    }

    this.changeRef.detectChanges();
  }

  async showLoader(): Promise<any> {
    if (this._loader) {
      this._loader.dismiss();
    }

    this._loader = await this.loadingCtrl.create({
      spinner: 'crescent',
      message: 'Verifying email...',
      backdropDismiss: false,
    });

    return this._loader.present();
  }

  private _dismissLoading(): void {
    if (this._loader) this._loader.dismiss();
  }

  public close(): void {
    this.modalCtrl.dismiss();
  }
}
